import React, { useMemo, useState } from 'react';
import SvgIcon from '@home/components/SvgIcon';
import BubbleCommon, {
  steamPerformanceHourlyAddedReviewsTips,
} from '@op/components/BubbleCommon';
import CardCommon from '@op/components/CardCommon';
import { useHistory, useIntl } from 'umi';
import SteamLineReviews from '@op/widgets/GameStorePlate/components/SteamLineReviews';
import { useSteamPercentage } from '../hooks';
import { formatThousandthValue, reviewsFormat } from '@op/utils/value';
import useGetPcConsoleStats from '@op/pages/gamestore/hooks/useGetPcConsoleStats';
import { Bubble, Col, Row, Segment } from 'tea-component';
import {
  stackLineIcon,
  downloadIcon,
  lineIcon,
  barIcon,
  commonBarTHIcon,
  barLineIcon,
} from '@op/constants/chart';
import { NO_DATE } from '@op/constants';
import ReviewsRealtime from '../components/ReviewsRealtime';
import SteamStackedTimeline from '../components/SteamStackedTimeline';
import { downloadAction } from '@op/components/DownloadCommon/download-page';
import { dealwithSelectedTimeline } from '../utils';
import PercentageItemCommon from '@op/components/PercentageItemCommon';
import RingRatioBubble from '@op/components/Snippets/RingRatioBubble';
import { isOneDay } from '@op/utils';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import '../scss/realtimeFilteredReviews.scss';
import steamPositive from '@op/assets/svg/steam_positive.svg';
import steamNegative from '@op/assets/svg/steam_negative.svg';
import SteamScoreTrendBox from '@op/pages/gamestore/widgets/ScoreTrendsPcConsole/SteamScoreTrendBox';

const defaultChannel: any = ['steam'];
export type TabType = 'all' | 'positive' | 'negative';
export const keyValueList: {
  [key in TabType]: {
    list: Array<{ name: string; color: string }>;
    icon?: string;
  };
} = {
  all: {
    list: [
      {
        name: 'This time period',
        color: '#5086F3',
      },
      {
        name: 'The previous corresponding period',
        color: '#A6C9FF',
      },
    ],
  },
  positive: {
    list: [
      {
        name: 'Positive',
        color: '#69D19C',
      },
      {
        name: 'The previous corresponding period',
        color: '#92DEB4',
      },
    ],
    icon: steamPositive,
  },
  negative: {
    list: [
      {
        name: 'Negative',
        color: '#F17C86',
      },
      {
        name: 'The previous corresponding period',
        color: '#FFABAF',
      },
    ],
    icon: steamNegative,
  },
};
export default function RealtimeFilteredReviews({
  timeline_of_start_time,
  timeline_of_end_time,
  list,
  old_list,
  loading,
  filter,
}: {
  releaseTime: string;
  timeline_of_start_time: string;
  timeline_of_end_time: string;
  list: any;
  old_list: any;
  loading: boolean;
  error: boolean;
  filter: any;
}) {
  const { formatMessage } = useIntl();
  const [chartType, setChartType] = useState('bar-line');
  const [tab, setTab] = useState<TabType>('all');

  const { userScore, userScoreLoading, userScoreApiError } =
    useGetPcConsoleStats({
      keepParams: filter,
      channel: defaultChannel,
      needLink: true,
    });

  const { steamRenderBar, steamRenderReviewTotal, time, percentInfo } =
    useSteamPercentage({
      keys: ['select_all_review', 'select_non_recommend', 'select_recommend'],
      data: userScore.steam,
      filter,
      isSelected: true,
      needLink: true,
    });

  const { oldValue, newValue } = useMemo(() => {
    if (tab === 'all') return dealwithSelectedTimeline(list, old_list);

    const { select_recommend, select_non_recommend } = list;
    const {
      select_recommend: oldSelectRecommend,
      select_non_recommend: oldNonSelectRecommend,
    } = old_list;

    if (tab === 'positive') {
      return { oldValue: oldSelectRecommend, newValue: select_recommend };
    }

    return {
      oldValue: oldNonSelectRecommend,
      newValue: select_non_recommend,
    };
  }, [list, old_list, tab]);

  const platformData = useMemo(() => {
    return {
      timeline_of_start_time,
      timeline_of_end_time,
      list,
    };
  }, [timeline_of_start_time, timeline_of_end_time, list]);

  function downloadReviewsInfo() {
    const name =
      chartType !== 'stack_line'
        ? 'Realtime Added Reviews'
        : 'Realtime Added Stacked Reviews';
    downloadAction({
      limit: 150000,
      downLoadParams: {
        ...filter,
        need_link: true,
        channel: ['steam'],
        channels: ['steam'],
        metrics: ['recommend', 'non_recommend'],
        ret_metrics: ['recommend', 'non_recommend'],
        types: ['total', 'slice'],
      },
      downloadFunc: 'getOverviewPcConsoleTimeline',
      simpleName: name,
      sheetName: name,
    });
  }

  const history = useHistory();
  function gotoFeeds() {
    goStatsDetailAction({
      path: `/opinion/Feeds/Feeds?tab=gameStore`,
      params: {
        statsDetailType: 'steam-RealtimeRatings',
        ...filter,
        channel: ['steam'],
        channel_type: 'comments',
      },
      history,
    });
  }

  const chartIconList = useMemo(() => {
    const list = [barIcon, stackLineIcon, lineIcon, downloadIcon];
    if (tab === 'all') {
      list.unshift(barLineIcon, commonBarTHIcon);
    }

    return list;
  }, [tab]);

  return (
    <CardCommon
      title={
        <div className="filter-reviews-head">
          <SvgIcon className="title-icon-info" type="new-logo"></SvgIcon>
          {formatMessage({ id: 'Hourly Added Reviews' })}
          <div className="time-info">{time}</div>
          <BubbleCommon
            tipsMap={steamPerformanceHourlyAddedReviewsTips}
            placement="top"
          ></BubbleCommon>
        </div>
      }
      className="realtime-filtered-reviews-wrapper"
      contentCard
      loading={loading || userScoreLoading}
      loadingHeight={336}
    >
      <Row>
        <Col span={16}>
          <div className="title-box">
            <div className="title-info">
              <h2 className="title">
                {reviewsFormat(steamRenderReviewTotal.review)}
              </h2>
              <Bubble
                closeOnScroll
                content={formatMessage({ id: 'View details' })}
                dark
              >
                <label className="share-icon">
                  <SvgIcon onClick={gotoFeeds} type="view-details-icon" />
                </label>
              </Bubble>
              <span className="dod-info">
                {formatMessage({
                  id: isOneDay(filter) ? 'DoD' : 'Change Rate',
                })}
                <RingRatioBubble
                  oldTime={percentInfo.oldTime}
                  newTime={percentInfo.newTime}
                  oldValue={percentInfo.oldValue}
                  newValue={percentInfo.newValue}
                >
                  <PercentageItemCommon
                    percentage={percentInfo.percentage}
                    value={percentInfo.newValue}
                    showValue={false}
                  ></PercentageItemCommon>
                </RingRatioBubble>
              </span>
            </div>
            <SteamLineReviews
              steamRenderReviewList={steamRenderBar}
              reviewTotal={steamRenderReviewTotal?.review || 0}
              formatNumber={formatThousandthValue}
              showReviewTotal={false}
            ></SteamLineReviews>
          </div>
        </Col>
      </Row>
      <Row>
        <Col span={16}>
          <Segment
            className="filter-reviews-segment"
            value={tab}
            onChange={(value: any) => {
              setTab(value);
              if (
                ['negative', 'positive'].includes(value) &&
                ['bar-line', 'common-th-bar'].includes(chartType)
              ) {
                setChartType('bar');
              }
            }}
            options={[
              { text: formatMessage({ id: 'All' }), value: 'all' },
              {
                text: (
                  <>
                    <i className="icon-positive"></i>
                    {formatMessage({ id: 'Positive' })}
                  </>
                ),
                value: 'positive',
              },
              {
                text: (
                  <>
                    <i className="icon-negative"></i>
                    {formatMessage({ id: 'Negative' })}
                  </>
                ),
                value: 'negative',
              },
            ]}
          />
        </Col>
        <Col span={8} className="left-col-box">
          {chartIconList.map(({ icon, symbol, tip, rotate = 0 }) => {
            return (
              <Bubble
                key={symbol}
                content={formatMessage({ id: tip || NO_DATE.EMPTY })}
                dark
                closeOnScroll
              >
                <span
                  className={`chart-icon-box ${symbol === chartType && 'active'
                    }`}
                  onClick={() => {
                    if (symbol !== 'download') setChartType(symbol);
                    else downloadReviewsInfo();
                  }}
                >
                  <SvgIcon
                    style={{ transform: `rotate(${rotate}deg)` }}
                    className={symbol}
                    type={icon}
                  />
                </span>
              </Bubble>
            );
          })}
        </Col>
      </Row>
      {['line', 'bar'].includes(chartType) ? (
        <ReviewsRealtime
          oldValue={oldValue}
          newValue={newValue}
          filter={filter}
          top={30}
          height={336}
          calculationMaxMin
          isSelected
          chartType={chartType}
          leftTitle={formatMessage({ id: 'Mentions' })}
          valueKey="review"
          {...keyValueList[tab]}
          showEndLabel
        ></ReviewsRealtime>
      ) : ['bar-line', 'common-th-bar'].includes(chartType) ? (
        <SteamScoreTrendBox
          platformData={platformData}
          filterParams={filter}
          height={336}
          negativeLessThanZero={chartType === 'common-th-bar'}
        ></SteamScoreTrendBox>
      ) : (
        <SteamStackedTimeline
          oldValue={oldValue}
          leftTitle={formatMessage({ id: 'Mentions' })}
          newValue={newValue}
          height={336}
          {...keyValueList[tab]}
          filter={filter}
        ></SteamStackedTimeline>
      )}
    </CardCommon>
  );
}
